import {bigWinStore} from '@gongxigames/framework/slotV2/modules/bigwin/bigWinStore';
import {GameOnlyEventMap, slotStore} from '@gongxigames/framework/slotV2/globals';
import {SlotModuleV2} from '@gongxigames/framework/slotV2/baseClasses/slotModuleV2/SlotModuleV2';
import {BigWinViewV2} from '@gongxigames/framework/slotV2/modules/bigwin/BigWinViewV2';
import {LAYERS} from '@gongxigames/framework/slot';

export class BigWinModuleV2<T extends GameOnlyEventMap> extends SlotModuleV2<T> {
    public static readonly NAME: symbol = Symbol('BigWinModule');
    public static layer = LAYERS.POPUPS;

    protected isSkippableImmediately = false;
    protected isPlayWinMeter = false;
    protected view!: BigWinViewV2;

    protected countFrom = 0;
    protected countTo = 0;
    protected bigWinType!: string;
    private bigWinResolve?: () => void;
    private regularCounterStartedResolve?: () => void;
    private regularCounterFinishedResolve?: () => void;

    protected onInit(): void {
        this.view = new BigWinViewV2({
            onBigWinFinished: this.onBigWinFinished.bind(this),
            onMeterStarted: this.onMeterStarted.bind(this),
            onMeterFinalValue: this.onMeterFinalValue.bind(this),
            onMeterFinished: this.onMeterFinished.bind(this)
        });
        this.container.addChild(this.view.container);
    }

    protected onSetupEvents(): void {
        super.onSetupEvents();
        this.emitter.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.onResourcesLoaded.bind(this));

        this.emitter.on('General.BigWin.WON_BIG_WIN', this.onRegularBigWinPromise.bind(this));
        this.emitter.on('General.BigWin.BIG_WIN_AFTER_FREE_SPINS_STARTED', this.onBigWinAfterFreeSpinsPromise.bind(this));
        this.emitter.on('General.BigWin.BIG_WIN_AFTER_RETRIGGER_STARTED', this.onBigWinAfterRetriggerPromise.bind(this));
        this.emitter.on('General.BigWin.WAIT_FOR_BIG_WIN_COUNTER_STARTED', this.onWaitForCounterStarted.bind(this));
        this.emitter.on('General.BigWin.WAIT_FOR_BIG_WIN_COUNTER_FINISHED', this.onWaitForCounterFinished.bind(this));
    }

    protected onResourcesLoaded(): void {
        this.view && this.view.onResourcesLoaded();
    }

    protected onMeterFinished(): void {
        this.regularCounterFinishedResolve?.();
    }

    protected onMeterStarted(): void {
        this.regularCounterStartedResolve?.();
    }

    protected onMeterFinalValue(): void {
        this.emitter.emit<'Module.command.WinMeter.SNAP_TO_FINAL_VALUE', void>('Module.command.WinMeter.SNAP_TO_FINAL_VALUE');
    }

    protected setupBigWinAfterFreeSpins(): void {
        this.isSkippableImmediately = false;
        this.isPlayWinMeter = false;
        this.countFrom = 0;
        this.countTo = slotStore.logic_totalWin.value;
        this.bigWinType = this.getBigWinType();
    }

    protected setupBigWinAfterRetrigger(): void {
        this.isSkippableImmediately = false;
        this.isPlayWinMeter = false;
        this.countFrom = this.previousWin();
        this.countTo = slotStore.logic_totalWin.value;
        this.bigWinType = this.getBigWinType();
    }

    protected previousWin(): number {
        return slotStore.logic_previousSpinRoundWin.value;
    }

    protected getBigWinType(): string {
        const win = this.countTo - this.countFrom;
        const bet = slotStore.logic_totalBet.value;
        const ratio = win / bet;
        const thresholds = Object.entries(bigWinStore.big_win_thresholds.value)
            .sort(([, v1], [, v2]) => v2 - v1);

        for (const [type, threshold] of thresholds) {
            if (ratio >= threshold) {
                return type;
            }
        }

        return thresholds[thresholds.length - 1][0];
    }

    protected setupRegularBigWin(): void {
        this.isSkippableImmediately = false;
        this.isPlayWinMeter = true;
        this.countFrom = this.previousWin();
        this.countTo = slotStore.logic_totalWin.value;
        this.bigWinType = this.getBigWinType();
    }

    protected onRegularBigWinPromise(): Promise<void> {
        return new Promise<void>(resolve => {
            this.bigWinResolve = resolve;
            this.setupRegularBigWin();
            this.playBigWinView();
        });
    }

    protected onBigWinAfterFreeSpinsPromise(): Promise<void> {
        return new Promise<void>(resolve => {
            this.bigWinResolve = resolve;
            this.setupBigWinAfterFreeSpins();
            this.playBigWinView();
        });
    }

    protected onBigWinAfterRetriggerPromise(): Promise<void> {
        return new Promise<void>(resolve => {
            this.bigWinResolve = resolve;
            this.setupBigWinAfterRetrigger();
            this.playBigWinView();
        });
    }

    protected playBigWinView(): void {
        this.view?.play(this.bigWinType, this.isSkippableImmediately,
            this.countFrom, this.countTo, this.isPlayWinMeter);
    }

    protected onWaitForCounterStarted(): Promise<void> {
        return new Promise<void>(resolve => {
            this.regularCounterStartedResolve = resolve;
        });
    }

    protected onWaitForCounterFinished(): Promise<void> {
        return new Promise<void>(resolve => {
            this.regularCounterFinishedResolve = resolve;
        });
    }

    protected onBigWinFinished(): void {
        this.bigWinResolve?.();
    }
}
